import { Divider, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { KebabTableMenuInitiatives } from 'screens/Initiatives/InitiativeAudit/AGGridComponents';

import { InitiativeIcon } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/homepage/noInitiativesSummaryCard.svg';

import { TASK_TYPES_2, TEAM_TYPE } from 'constants/ProjectConstants';
import { getTeamPageForInitiativesRoute } from 'constants/helper';
import { sendOpenedKRPanelEvent } from 'constants/mixpanelHelper';

import { showTaskDetails } from 'services/taskDetails';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Button } from 'uiToolkitV1/Button';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { MySummaryCardTemplate } from '../MySummaryCardTemplate';
import { useProfileAndSummaryInitiativesList } from './hooks';

export const TASK_STATUS_KEYS = {
  COMPLETED: {
    key: 'COMPLETED',
    label: 'Completed',
    color: '#1A5724',
    tagColor: '#DBF2D3',
  },
  ONGOING: {
    key: 'ONGOING',
    label: 'Ongoing',
    color: '#44812F',
    tagColor: '#EFFFEA',
  },
  DELAYED: {
    key: 'DELAYED',
    label: 'Delayed',
    color: '#C62C02',
    tagColor: '#FFF1E4',
  },
  UNPLANNED: {
    key: 'UNPLANNED',
    label: 'Unplanned',
    color: 'text.g1',
    tagColor: '#EEEEEE',
  },
  COMPLETED_ON_TIME: {
    key: 'COMPLETED_ON_TIME',
    label: 'Completed',
    color: '#1A5724',
    tagColor: '#DBF2D3',
  },
  COMPLETED_WITH_DELAY: {
    key: 'COMPLETED_WITH_DELAY',
    label: 'Completed with delay',
    color: '#1A5724',
    tagColor: '#DBF2D3',
  },
};

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;
`;

const StyledAvatarGroup = styled(AvatarGroup)`
  display: flex;
`;
const ViewDetailsButton = styled(Button)`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;
const ButtonContainer = styled('div')`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;

export const GoalsMySummaryRowProfileAndHome = ({
  id,
  goalName,
  taskStatus,
  subTasksCount = 0,
  dueDate,
  employees = [],
  showDivider,
  accessPrivileges,
  visibilityType,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const showDetails = ({ index, taskId, type, originalGoal }) => {
    sendOpenedKRPanelEvent({
      eventProperties: {
        source: 'Home Page',
      },
    });
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete: null,
        employee: {},
        type: TASK_TYPES_2.task,
        history,
      })
    );
  };

  const DateText = ({ children }) => <TextBodyMedium color="text.g2">{children}</TextBodyMedium>;

  const employeesCollaboratorsFirst = employees?.sort((a, b) => {
    if (a.ownerType < b.ownerType) {
      return 1;
    }
    if (a.ownerType > b.ownerType) {
      return -1;
    }
    return 0;
  });

  return (
    <>
      <Container>
        <Flex
          width="100%"
          alignItems="center"
          onClick={(e) => {
            showDetails({ taskId: id });
          }}
        >
          <Flex flexDir="column" width="100%">
            <TextBodyMedium className="highlightTextOnHover">{goalName}</TextBodyMedium>
            <Flex mt="20px" alignItems="center">
              <Flex minHeight="28px" alignItems="center">
                <TextBodyMedium color={TASK_STATUS_KEYS[taskStatus]?.color} bold>
                  {TASK_STATUS_KEYS[taskStatus]?.label} &nbsp;
                </TextBodyMedium>

                {(subTasksCount > 0 || dueDate) && <TextBodyMedium color="text.g2">• &nbsp;</TextBodyMedium>}

                {subTasksCount > 0 && (
                  <TextBodyMedium color="text.g2">
                    {subTasksCount} {subTasksCount > 1 ? 'Sub Tasks' : 'Sub Task'} &nbsp; • &nbsp;
                  </TextBodyMedium>
                )}
                {dueDate && (
                  <DateComp
                    date={dueDate}
                    isRelativeDateFormat
                    enableRelativeFormatDistanceColor
                    textComponent={DateText}
                    forceRelativeDateFormat
                    showDateInBrackets
                  />
                )}
              </Flex>
              <StyledAvatarGroup ml="auto">
                {employeesCollaboratorsFirst?.map(({ displayName, profilePhoto, id, ownerType }) => (
                  <Avatar
                    sx={{ border: ownerType === 'OWNER' ? '1px solid #60C861 !important' : '0' }}
                    name={displayName}
                    src={profilePhoto}
                    key={id}
                  />
                ))}
              </StyledAvatarGroup>

              {/* <ButtonContainer className="showOnHover">
                <ViewDetailsButton
                  onClick={() => {
                    showDetails({ taskId: id });
                  }}
                  variant={'rowButtonOutlined'}
                  StartIcon={OpenSidePanel}
                >
                  View Details
                </ViewDetailsButton>
              </ButtonContainer> */}
            </Flex>
          </Flex>
        </Flex>
        <KebabTableMenuInitiatives
          data={{ uuid: id, visibilityType }}
          value={Array.isArray(accessPrivileges) ? accessPrivileges.map(({ name }) => name) : []}
        />
      </Container>
      {showDivider && <Divider color="neutral.CFD7DF" />}
    </>
  );
};

const getURLHref = ({ teams = [] }) => {
  const personalTeam = teams?.find((team, index) => {
    if (team?.teamType === TEAM_TYPE.personal) {
      return team;
    }
  });
  const url = getTeamPageForInitiativesRoute({
    teamId: personalTeam?.id,
    teamName: 'My Initiatives',
    teamType: 'MY_INITIATIVES',
  });
  return url;
};

const InitiativesMySummaryCard = ({ employeeId }) => {
  const {
    user: { id, teams },
  } = useAuthenticatedUser();
  const { data, isLoading, isError } = useProfileAndSummaryInitiativesList({
    limit: 3,
    offset: 0,
    attributeFilter: {
      filterConditions: [{ data: [employeeId ?? id], operatorType: 'IN', type: 'NAME' }],
    },

    pageType: employeeId ? 'PROFILE_PAGE' : 'HOME_PAGE',
    resourceType: 'LIST',
  });

  if (isLoading) {
    return (
      <MySummaryCardTemplate showViewAllButton={false} Icon={InitiativeIcon} heading="Initiatives">
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </MySummaryCardTemplate>
    );
  }

  if (!data?.computedData?.length) {
    return (
      <EmptySummaryCard
        Icon={InitiativeIcon}
        heading="Initiatives"
        description="The best way to have a garden full of roses is to plant them! Take Initiative and get started today!"
        illustrationSrc={NoDataImg}
      />
    );
  }

  return (
    <MySummaryCardTemplate
      Icon={InitiativeIcon}
      heading="Initiatives"
      viewAllText="View All Initiatives"
      viewAllHref={getURLHref({ teams })}
    >
      {data?.computedData
        ?.slice(0, 3)
        .map(({ uuid, title, status, subTasksCount, dueTime, toIds, accessPrivileges, visibilityType }, index) => (
          <GoalsMySummaryRowProfileAndHome
            goalName={title}
            taskStatus={status}
            subTasksCount={subTasksCount}
            dueDate={dueTime?.timeStamp}
            employees={toIds}
            accessPrivileges={accessPrivileges}
            visibilityType={visibilityType}
            showDivider
            id={uuid}
            key={uuid}
          />
        ))}
    </MySummaryCardTemplate>
  );
};

export { InitiativesMySummaryCard };
